import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { CompanyChip, Notice } from "../../design/components";
import { dateTime, omr } from "../../lib/format";
import type { InboundDocumentRow } from "../../api/inbound";

/** The receiving team posts the draft; the hub never posts it. */
export const ERP_DRAFT_STATE = "Draft — awaiting review";

/** m4-rulings.md #94: a Method 3 document has no ERP draft — it is delivered to the outbox instead. */
export const ERP_FILE_DELIVERED_STATE = "Delivered to the results folder";

/** A delivered Method 3 row's erpDocId is the outbox filename, not an ERP doc id. */
function isFileDelivered(row: InboundDocumentRow): boolean {
  return row.erpDocId != null && row.erpDocId.endsWith(".xml");
}

export function LandedTable({ rows, isLoading }: { rows: InboundDocumentRow[]; isLoading: boolean }) {
  const { t } = useTranslation();
  if (!rows.length && !isLoading) {
    return (
      <div className="card-body">
        <Notice kind="info">{t("inbound.landed.empty", "No landed documents in this period.")}</Notice>
      </div>
    );
  }
  return (
    <div className="table-wrap">
      <table className="tbl" aria-label={t("inbound.landed.tableLabel", "Landed")}>
        <thead>
          <tr>
            <th className="nowrap">{t("inbound.landed.colReceived", "Received")}</th>
            <th>{t("inbound.landed.colSupplier", "Supplier")}</th>
            <th>{t("inbound.landed.colInvoiceNumber", "Invoice number")}</th>
            <th>{t("inbound.landed.colCompany", "Company")}</th>
            <th className="r">{t("inbound.landed.colTotal", "Total")}</th>
            <th>{t("inbound.landed.colErpReference", "ERP reference")}</th>
            <th>{t("inbound.landed.colErpState", "ERP state")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="nowrap">{dateTime(row.createdAt)}</td>
              <td>{row.counterparty ?? "—"}</td>
              <td>
                <Link className="link" to={`/inbound/${row.id}`}>
                  {row.invoiceNumber ?? row.id}
                </Link>
              </td>
              <td>
                <CompanyChip entityId={row.entityId} name={row.entityName} code={row.entityCode} />
              </td>
              <td className="r nowrap">
                {omr(row.totalAmount)}
                {row.currency ? ` ${row.currency}` : ""}
              </td>
              <td className="mono nowrap">{row.erpDocId ?? "—"}</td>
              <td className="nowrap">
                {isFileDelivered(row)
                  ? t("inbound.landed.erpFileDelivered", ERP_FILE_DELIVERED_STATE)
                  : t("inbound.landed.erpDraftState", ERP_DRAFT_STATE)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
